import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PiArrowRightBold,
  PiCalendarLight,
  PiCheckCircleLight,
  PiClockLight,
  PiRepeatLight,
  PiStudentLight,
  PiWarningCircleLight,
} from "react-icons/pi";
import client from "../../api/client";

function AddMakeupClassModal({ classrooms, onClose, onCreated }) {
  const [form, setForm] = useState({ classroom: "", topic: "", start_time: "", link: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.classroom || !form.topic || !form.start_time) {
      toast.error("Classroom, topic and date/time are required.");
      return;
    }
    const [cohort_id, course_id] = form.classroom.split("|");
    setSaving(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      await client.post("/schedules", {
        topic: form.topic,
        start_time: form.start_time,
        link: form.link || undefined,
        cohort_id,
        course_id,
        type: "makeup",
      });
      toast.success("Makeup class created.");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not create makeup class.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-800">🔁 Add Makeup Class</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Classroom *</label>
            <select required value={form.classroom} onChange={(e) => setForm((p) => ({ ...p, classroom: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]">
              <option value="">Select classroom</option>
              {classrooms.map((cl) => (
                <option key={`${cl.course_id}-${cl.cohort_id}`} value={`${cl.cohort_id}|${cl.course_id}`}>
                  {cl.course_title} {cl.cohort_name ? `— ${cl.cohort_name}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Topic *</label>
            <input type="text" required value={form.topic} onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))}
              placeholder="e.g. Makeup: Introduction to APIs"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Date & Time *</label>
            <input type="datetime-local" required value={form.start_time} onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Meeting Link</label>
            <input type="url" value={form.link} onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))}
              placeholder="https://meet.google.com/..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]" />
          </div>
          <p className="text-xs text-gray-400">Shows on the student dashboard for everyone in this classroom, like a normal class — attendance isn't tracked.</p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold">
              {saving ? "Creating…" : "Create Makeup Class"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function InstructorDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMakeupModal, setShowMakeupModal] = useState(false);

  const loadOverview = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    client
      .get("/instructor/overview")
      .then((res) => setOverview(res.data))
      .catch(() => toast.error("Failed to load instructor overview."))
      .finally(() => setLoading(false));
  };

  useEffect(loadOverview, []);

  if (loading) {
    return (
      <div className="space-y-5 animate-pulse">
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-2xl" />)}
        </div>
        <div className="h-36 bg-gray-100 rounded-2xl" />
        <div className="h-48 bg-gray-100 rounded-2xl" />
      </div>
    );
  }

  const session      = overview?.next_session;
  const isLive       = session?.status === "live";
  const classrooms   = overview?.classrooms ?? [];
  const recentUngrd  = overview?.recent_ungraded ?? [];

  return (
    <div className="space-y-6">

      {showMakeupModal && (
        <AddMakeupClassModal
          classrooms={classrooms}
          onClose={() => setShowMakeupModal(false)}
          onCreated={loadOverview}
        />
      )}

      {/* ── Quick actions ─────────────────────────────────────────────────────── */}
      {classrooms.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowMakeupModal(true)}
            className="text-xs font-semibold bg-violet-50 hover:bg-violet-100 text-violet-700 px-4 py-2.5 rounded-xl transition-colors flex items-center gap-1.5"
          >
            <PiRepeatLight size={14} />
            Add Makeup Class
          </button>
        </div>
      )}

      {/* ── 3 stat cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
            <PiCalendarLight size={20} className="text-[#d15424]" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 leading-none">{overview?.total_sessions ?? 0}</p>
            <p className="text-xs text-gray-500 mt-0.5 font-medium">Classes</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <PiStudentLight size={20} className="text-[#294a93]" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 leading-none">{overview?.total_students ?? 0}</p>
            <p className="text-xs text-gray-500 mt-0.5 font-medium">Students</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
            <PiWarningCircleLight size={20} className="text-rose-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 leading-none">{overview?.pending_submissions ?? 0}</p>
            <p className="text-xs text-gray-500 mt-0.5 font-medium">Pending Grades</p>
          </div>
        </div>
      </div>

      {/* ── Assigned Classrooms ───────────────────────────────────────────────── */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
          My Assigned Classroom{classrooms.length !== 1 ? "s" : ""}
        </h3>
        {classrooms.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 py-10 text-center shadow-sm">
            <p className="text-3xl mb-2">👨‍🏫</p>
            <p className="text-sm font-medium text-gray-600">No classrooms assigned yet</p>
            <p className="text-xs text-gray-400 mt-1">Contact an admin to be assigned to a course.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {classrooms.map((cl) => (
              <div
                key={`${cl.course_uuid}-${cl.cohort_id}`}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex items-center gap-4 p-4 hover:shadow-md transition-shadow"
              >
                {/* Course thumbnail */}
                <div className="w-16 h-16 rounded-xl bg-slate-100 flex-shrink-0 overflow-hidden">
                  {cl.course_image ? (
                    <img src={cl.course_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#294a93] to-[#3b60c4] flex items-center justify-center text-white text-2xl">
                      📚
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm leading-snug truncate">{cl.course_title}</p>
                  {cl.cohort_name && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{cl.cohort_name}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Link
                      to={`/dashboard/course/${cl.course_uuid}?cohort_id=${cl.cohort_id}`}
                      className="text-xs font-semibold bg-[#294a93] hover:bg-[#1e3a7a] text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                    >
                      Enter Classroom <PiArrowRightBold size={11} />
                    </Link>
                  </div>
                </div>

                {/* Quick actions */}
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => navigate("/dashboard/instructor/submissions")}
                    className="text-xs bg-orange-50 hover:bg-orange-100 text-[#d15424] font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <PiCheckCircleLight size={13} />
                    Grade
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Next / Live session ───────────────────────────────────────────────── */}
      {session && (
        <div
          className={`rounded-2xl p-4 text-white ${
            isLive
              ? "bg-gradient-to-r from-red-600 to-rose-500"
              : "bg-gradient-to-r from-[#294a93] to-[#3b60c4]"
          }`}
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {isLive ? (
                  <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    LIVE NOW
                  </span>
                ) : (
                  <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">NEXT CLASS</span>
                )}
                {session.course && <span className="text-xs opacity-80">{session.course}</span>}
              </div>
              <p className="font-semibold leading-snug">{session.topic || "Class Session"}</p>
              <p className="text-sm opacity-80 mt-0.5 flex items-center gap-1">
                <PiClockLight size={13} />
                {formatDate(session.start_time)} · {formatTime(session.start_time)}–{formatTime(session.end_time)}
              </p>
            </div>
            {session.link && isLive && (
              <a
                href={session.link}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl bg-white text-[#294a93] text-xs font-bold hover:bg-white/90 transition-colors"
              >
                Join Class
              </a>
            )}
          </div>
        </div>
      )}

      {/* ── Recent ungraded submissions ────────────────────────────────────────── */}
      {recentUngrd.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <p className="font-semibold text-gray-800 text-sm">Ungraded Submissions</p>
            <button
              onClick={() => navigate("/dashboard/instructor/submissions")}
              className="text-xs text-[#d15424] font-medium hover:underline flex items-center gap-1"
            >
              View all <PiArrowRightBold size={11} />
            </button>
          </div>
          <ul className="divide-y divide-gray-50">
            {recentUngrd.map((sub) => (
              <li key={sub.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{sub.student_name}</p>
                  <p className="text-xs text-gray-400 truncate">{sub.project_title}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-400">{timeAgo(sub.submitted_at)}</span>
                  <button
                    onClick={() => navigate("/dashboard/instructor/submissions")}
                    className="text-xs bg-orange-50 text-[#d15424] font-semibold px-3 py-1 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    Grade
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Grade submissions CTA (when nothing pending) ──────────────────────── */}
      {recentUngrd.length === 0 && overview?.pending_submissions === 0 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center gap-3">
          <PiCheckCircleLight size={22} className="text-emerald-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">All caught up!</p>
            <p className="text-xs text-emerald-600 mt-0.5">No submissions waiting to be graded.</p>
          </div>
        </div>
      )}
    </div>
  );
}
